"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Menu, X, Radio, Rocket } from "lucide-react";
import { isComingSoon } from "@/lib/coming-soon";

const navLinks = [
    { label: "Features", href: "/#features" },
    { label: "Pricing", href: "/pricing" },
];

export function Navbar() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <nav
            className={`fixed top-0 z-50 w-full transition-all duration-300 ${
                scrolled
                    ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm"
                    : "bg-transparent"
            }`}
        >
            <div className="container mx-auto flex h-14 items-center justify-between px-4">
                {/* Logo */}
                <Link href="/" className="flex items-center gap-2">
                    <div className="relative flex h-7 w-7 items-center justify-center rounded-md bg-primary/15">
                        <Radio className="h-3.5 w-3.5 text-primary" />
                        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-status-healthy pulse-healthy" />
                    </div>
                    <span className="font-mono text-lg font-bold tracking-tight">
                        Ping<span className="text-primary">NG</span>
                    </span>
                </Link>

                {/* Desktop links */}
                <div className="hidden items-center gap-6 md:flex">
                    {navLinks.map((link) => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>

                {/* Desktop auth */}
                <div className="hidden items-center gap-3 md:flex">
                    {isComingSoon ? (
                        <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs">
                            <Rocket className="h-3 w-3" />
                            Launching Soon
                        </Badge>
                    ) : (
                        <>
                            <Button variant="ghost" size="sm" asChild>
                                <Link href="/login">Log in</Link>
                            </Button>
                            <Button size="sm" className="glow-green" asChild>
                                <Link href="/register">Get Started</Link>
                            </Button>
                        </>
                    )}
                </div>

                {/* Mobile toggle */}
                <button
                    className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground md:hidden cursor-pointer"
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {/* Mobile menu */}
            {menuOpen && (
                <div className="border-t border-border/50 bg-background/95 backdrop-blur-xl px-4 pb-6 pt-4 md:hidden">
                    <div className="flex flex-col gap-3">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                onClick={() => setMenuOpen(false)}
                                className="text-sm text-muted-foreground hover:text-foreground"
                            >
                                {link.label}
                            </Link>
                        ))}
                        <div className="mt-3 flex flex-col gap-2 border-t border-border/50 pt-3">
                            {isComingSoon ? (
                                <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs justify-center">
                                    <Rocket className="h-3 w-3" />
                                    Launching Soon
                                </Badge>
                            ) : (
                                <>
                                    <Button variant="outline" size="sm" asChild>
                                        <Link href="/login">Log in</Link>
                                    </Button>
                                    <Button size="sm" asChild>
                                        <Link href="/register">Get Started</Link>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}
